import React, {useState, useEffect, useCallback, useRef} from 'react';
import {ChevronLeft, ChevronRight, Loader, Trash2} from 'lucide-react';
import CatalogSearchBar from "../CatalogSearchBar";
import RemiseEditor from './RemiseEditor';
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {URLS_UPLOADS} from "../../../config/url";

const RemiseCategories = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // ── URL-persisted state ──────────────────────────────────────────
    const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
    const searchTerm = searchParams.get('q') || '';
    // Filtre de statut : '' (tous) | 'true' (actives) | 'false' (inactives)
    const activeFilter = ['true', 'false'].includes(searchParams.get('active')) ? searchParams.get('active') : '';

    // Local input for debouncing
    const [inputValue, setInputValue] = useState(() => searchParams.get('q') || '');

    // Sync input when URL changes externally (e.g. browser Back)
    useEffect(() => {
        setInputValue(searchParams.get('q') || '');
    }, [searchParams]);

    const updateParams = useCallback((updates, push = false) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v === '' || v === null || v === undefined) next.delete(k);
                else next.set(k, String(v));
            });
            return next;
        }, {replace: !push});
    }, [setSearchParams]);

    const setCurrentPage = useCallback((page) => {
        updateParams({page}, true);
    }, [updateParams]);

    // Debounce: write search to URL after 600 ms, reset page to 1
    useEffect(() => {
        const timer = setTimeout(() => {
            updateParams({q: inputValue || null, page: null});
        }, 600);
        return () => clearTimeout(timer);
    }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const [selectedAll, setSelectedAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [editRemise, setEditRemise] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [remiseData, setRemiseData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [error, setError] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const selectionBarRef = useRef(null);

    // Fetch discounts from API with search parameter
    const fetchRemises = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const accountId = await secureStorage.getItem("selectedAccount");

            // Add search parameter if searchTerm exists
            const searchParam = searchTerm ? `&name=${encodeURIComponent(searchTerm)}` : '';
            const activeParam = activeFilter ? `&active=${activeFilter}` : '';

            const response = await api.get(`/discounts?page=${currentPage}${searchParam}${activeParam}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            setRemiseData(response.data.data);
            setTotalPages(response.data.pagination.totalPages);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching discounts:", error);
            setError("Impossible de charger les remises");
            setIsLoading(false);
        }
    }, [currentPage, searchTerm, activeFilter]);

    // Initial data fetch and when page or search changes
    useEffect(() => {
        fetchRemises();
    }, [fetchRemises]);

    // Handle page navigation
    const goToNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const goToPrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? remiseData.map(item => item.id) : []);
    };

    const handleSelectItem = (id, event) => {
        event.stopPropagation();

        setSelectedItems(prevSelected =>
            prevSelected.includes(id)
                ? prevSelected.filter(itemId => itemId !== id)
                : [...prevSelected, id]
        );
    };

    // Show confirmation dialog instead of deleting immediately
    const handleDeleteSelected = () => {
        if (selectedItems.length > 0) {
            setShowConfirmDialog(true);
        }
    };

    // Cancel delete action
    const cancelDelete = () => {
        setShowConfirmDialog(false);
    };

    // Confirm delete action
    const confirmDelete = async () => {
        try {
            setIsDeleting(true);
            const accountId = await secureStorage.getItem("selectedAccount");

            const deletionPromises = selectedItems.map(id =>
                api.delete(`/discounts/${id}`, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json'
                    }
                })
            );

            await Promise.all(deletionPromises);

            fetchRemises();

            setSelectedItems([]);
            setSelectedAll(false);

            setShowConfirmDialog(false);
            setIsDeleting(false);
        } catch (error) {
            setError("Une erreur s'est produite lors de la suppression");
            setShowConfirmDialog(false);
            setIsDeleting(false);
        }
    };

    const handleEditRemise = (remise) => {
        navigate(`/catalogue/discount/edit/${remise.id}`, {state: {returnTo: location.pathname + location.search}});
    };

    const handleBackToRemises = () => {
        setShowEditor(false);
        fetchRemises();
    };

    // Format the discount value for display
    const formatDiscountValue = (value, type) => {
        if (type === '%') {
            return `${value} %`;
        } else {
            return `${value} DH`;
        }
    };

    return (
        <div>
            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="card shadow-xl p-6 max-w-md w-full m-4 selection-bar-appear">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Confirmation de suppression</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Êtes-vous sûr de vouloir
                            supprimer {selectedItems.length} remise{selectedItems.length > 1 ? 's' : ''} ?
                            Cette action est irréversible.
                        </p>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                className="btn-ghost"
                                onClick={cancelDelete}
                                disabled={isDeleting}
                            >
                                Annuler
                            </button>
                            <button
                                className="btn-danger"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Suppression...' : 'Confirmer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remises List */}
            {!showEditor && (
                <div>
                    {/* Search Bar — même style que l'onglet Produits */}
                    <CatalogSearchBar
                        value={inputValue}
                        onChange={setInputValue}
                        activeSearch={searchTerm}
                        status={activeFilter}
                        onStatusChange={(value) => updateParams({active: value || null, page: null})}
                        onClearAll={() => {
                            setInputValue('');
                            updateParams({q: null, active: null, page: null});
                        }}
                        onClear={() => {
                            setInputValue('');
                            updateParams({q: null, page: null});
                        }}
                    />

                    {/* Selection Bar */}
                    {selectedItems.length > 0 && (
                        <div
                            ref={selectionBarRef}
                            className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20
                                border-l-4 border-primary shadow-sm selection-bar-appear"
                        >
                            <div className="flex items-center">
                                <div className="w-6 h-6 bg-primary flex items-center justify-center rounded-sm mr-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white"
                                         viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </div>
                                <span
                                    className="text-gray-700 dark:text-gray-200">{selectedItems.length} remise{selectedItems.length > 1 ? 's' : ''} sélectionnée{selectedItems.length > 1 ? 's' : ''}</span>
                            </div>
                            <button
                                onClick={handleDeleteSelected}
                                className="flex items-center px-3 py-1.5 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-colors duration-200"
                                disabled={isDeleting}
                            >
                                <Trash2 className="w-5 h-5 mr-1.5"/>
                                <span>Supprimer la sélection</span>
                            </button>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex justify-center items-center h-40">
                            <Loader className="w-8 h-8 text-primary animate-spin"/>
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-500 p-4">{error}</div>
                    ) : (
                        <>
                            {/* Table */}
                            <div className="card max-h-[430px] overflow-x-auto overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900/40">
                                    <tr>
                                        <th className="w-4 px-4 py-3">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-primary"
                                                checked={selectedAll}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                            NOM
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                            VALEUR
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                            TYPE DE CLIENT
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            STATUT
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {remiseData.map((item) => (
                                        <tr
                                            key={item.id}
                                            className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            onClick={() => handleEditRemise(item)}
                                        >
                                            <td className="w-4 px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-primary"
                                                    checked={selectedItems.includes(item.id)}
                                                    onChange={(e) => handleSelectItem(item.id, e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        className="w-8 h-8 rounded flex items-center justify-center text-white text-xs overflow-hidden"
                                                        style={{backgroundColor: item.color || '#4fb7ad'}}
                                                    >
                                                        {item.imageUrl && item.imageUrl !== '' ? (
                                                            <img
                                                                src={item.thumbnailUrl || (item.imageUrl.startsWith('http')
                                                                    ? item.imageUrl
                                                                    : `${URLS_UPLOADS}${item.imageUrl}`)
                                                                }
                                                                alt={item.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            item.name.substring(0, 2).toUpperCase()
                                                        )}
                                                    </div>
                                                    <span className="text-gray-800 dark:text-gray-100">{item.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDiscountValue(item.value, item.type)}</td>
                                            <td className="px-4 py-3">
                                                {item.clientTypes?.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {item.clientTypes.map((ct) => (
                                                            <span key={ct.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                                                {ct.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">Aucun</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs ${
                                                    item.active ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                                }`}>
                                                    {item.active ? 'Actif' : 'Inactif'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center mt-4 space-x-2">
                                    <button
                                        onClick={goToPrevPage}
                                        disabled={currentPage === 1}
                                        className={`p-2 rounded-full ${
                                            currentPage === 1
                                                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        <ChevronLeft className="w-5 h-5"/>
                                    </button>
                                    <span className="flex items-center px-4 text-gray-700 dark:text-gray-300">
                                        {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        onClick={goToNextPage}
                                        disabled={currentPage === totalPages}
                                        className={`p-2 rounded-full ${
                                            currentPage === totalPages
                                                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        <ChevronRight className="w-5 h-5"/>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* RemiseEditor Component */}
            {showEditor && <RemiseEditor remise={editRemise} onClose={handleBackToRemises}/>}
        </div>
    );
};

export default RemiseCategories;
